import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface EmptyStateAction {
  label: string;
  /** Navigation target. Omit when using onClick (e.g. an inline mutation). */
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  primary?: boolean;
}

/**
 * Standard guided empty state. Instead of a bare "No X yet", every zero-data
 * surface gets a title, a hint, and a primary/secondary next action that takes
 * the user to whatever populates it (upload a doc, connect a source, run an
 * agent, configure provisioning).
 */
export function EmptyState({
  icon,
  title,
  description,
  actions,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  actions?: EmptyStateAction[];
}) {
  return (
    <div className="rounded-2xl border border-dashed border-ink-200 bg-paper-50/40 p-10 text-center">
      {icon && <div className="mb-3 flex justify-center text-ink-400">{icon}</div>}
      <h3 className="text-[15px] font-semibold text-ink-900">{title}</h3>
      {description && (
        <p className="mt-1.5 text-[13px] text-ink-600 max-w-md mx-auto leading-relaxed">{description}</p>
      )}
      {actions && actions.length > 0 && (
        <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
          {actions.map((a) => {
            const cls = a.primary
              ? 'btn-press px-4 py-2 text-sm disabled:opacity-50'
              : 'px-4 py-2 text-sm text-accent-600 hover:underline disabled:opacity-50';
            return a.to ? (
              <Link key={a.label} to={a.to} className={cls}>
                {a.label}
              </Link>
            ) : (
              <button key={a.label} type="button" onClick={a.onClick} disabled={a.disabled} className={cls}>
                {a.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
